import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { FileText, ChevronLeft, ChevronRight, Shuffle, List, Grid, X } from 'lucide-react';
import { getQuestionsByExamId } from '../utils/questionAPI';
import { getSubjectById } from '../utils/subjectsAPI';

/**
 * 获取题型名称
 * @param {string} type - 题型代码
 * @returns {string} 题型名称
 */
const getTypeName = (type) => {
  switch(type) {
    case '1': 
    case '单选': return '单选题';
    case '2': 
    case '判断': return '判断题';
    case '3': 
    case '多选': return '多选题';
    default: return `${type}`;
  }
};

/**
 * 获取题型排序权重
 * @param {string} type - 题型
 * @returns {number} 排序权重(数字越小优先级越高)
 */
const getTypeWeight = (type) => {
  if (type === '单选' || type === '1') return 1;
  if (type === '判断' || type === '2') return 2;
  if (type === '多选' || type === '3') return 3;
  return 99; // 其他题型放最后
};

/**
 * 题目练习组件
 * 用于展示练习模式下的题目，支持查看答案和解析
 * @param {Object} session - 用户会话信息
 */
function QuestionPractice({ session }) {
  const { subjectId } = useParams();
  const [subject, setSubject] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [originalQuestions, setOriginalQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [answeredStatus, setAnsweredStatus] = useState({}); // 记录每个题目的答题状态
  const [loading, setLoading] = useState(true);
  const [isRandomMode, setIsRandomMode] = useState(false);
  const [showQuestionSelector, setShowQuestionSelector] = useState(false);
  const navigate = useNavigate();

  /**
   * 根据题型对题目进行排序
   * @param {Array} questionsData - 题目数据
   * @returns {Array} 排序后的题目数据
   */
  const sortQuestionsByType = (questionsData) => {
    return [...questionsData].sort((a, b) => {
      const aWeight = getTypeWeight(a.typename || a.typecode);
      const bWeight = getTypeWeight(b.typename || b.typecode);
      return aWeight - bWeight;
    });
  };

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        
        // 检查用户身份
        if (!session && !localStorage.getItem('user_id')) {
          navigate('/login');
          return;
        }
        
        // 获取学科信息
        const subjectData = await getSubjectById(subjectId);
        if (!subjectData) throw new Error('未找到学科信息');
        setSubject(subjectData);
        
        // 获取题目列表
        const questionsData = await getQuestionsByExamId(subjectId);
        
        // 保存原始顺序的题目
        setOriginalQuestions(questionsData || []);
        
        // 在顺序模式下按题型排序
        const sortedQuestions = sortQuestionsByType(questionsData || []);
        setQuestions(sortedQuestions);
        
        // 初始化用户答案
        const initialAnswers = {};
        questionsData.forEach(q => {
          initialAnswers[q._id] = '';
        });
        setUserAnswers(initialAnswers);
        
      } catch (error) {
        console.error('Error fetching questions:', error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchQuestions();
  }, [subjectId, session, navigate]);

  /**
   * 切换练习模式（顺序/随机）
   */
  const togglePracticeMode = () => {
    if (isRandomMode) {
      // 如果当前是随机模式，切换回顺序模式
      const sortedQuestions = sortQuestionsByType(originalQuestions);
      setQuestions(sortedQuestions);
      setIsRandomMode(false);
    } else {
      // 如果当前是顺序模式，切换到随机模式
      const shuffledQuestions = [...originalQuestions].sort(() => Math.random() - 0.5);
      setQuestions(shuffledQuestions);
      setIsRandomMode(true);
    }
    // 重置当前索引
    setCurrentIndex(0);
  };

  /**
   * 处理用户选择答案
   * @param {string} questionId - 题目ID 
   * @param {string} optionCode - 选择的选项代码
   * @param {boolean} isCorrect - 是否正确答案
   */
  const handleAnswer = (questionId, optionCode, isCorrect) => {
    // 记录用户答案
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: optionCode
    }));
    
    // 记录答题状态
    setAnsweredStatus(prev => ({
      ...prev,
      [questionId]: isCorrect ? 'correct' : 'incorrect'
    }));
  };

  /**
   * 处理下一题
   */
  const handleNextQuestion = () => {
    // 如果还有下一题，前进
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // 已经是最后一题，可以返回题库
      navigate('/question-bank');
    }
  };

  /**
   * 处理上一题
   */
  const handlePrevQuestion = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  /**
   * 切换题目选择器显示状态
   */
  const toggleQuestionSelector = () => {
    setShowQuestionSelector(!showQuestionSelector);
  };

  /**
   * 直接跳转到指定题目
   * @param {number} index - 题目索引
   */
  const jumpToQuestion = (index) => {
    setCurrentIndex(index);
    setShowQuestionSelector(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  // 适配options字段，因为新的数据结构可能与原来不同
  const getOptions = (question) => {
    if (!question) return [];
    
    // 检查options的格式并适配
    if (Array.isArray(question.options)) {
      return question.options;
    } else if (typeof question.options === 'object') {
      // 转换对象格式的options为数组
      return Object.entries(question.options).map(([code, content]) => ({
        code,
        content: typeof content === 'string' ? content : content.content || content.text || '',
        value: content.value || (content.isCorrect ? "1" : "0") || "0"
      }));
    } else {
      return [];
    }
  };

  const options = currentQuestion ? getOptions(currentQuestion) : [];
  
  // 是否显示当前题目的解析 - 如果已经回答过则显示解析
  const shouldShowExplanation = currentQuestion && userAnswers[currentQuestion._id];
  
  // 按题型分组题目
  const questionsByType = questions.reduce((acc, question, index) => {
    const type = question.typename || question.typecode || '未知';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push({ ...question, index });
    return acc;
  }, {});

  // 题型顺序
  const typeOrder = ['单选', '1', '判断', '2', '多选', '3'];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header title={subject?.name || '题目练习'} />
      
      <div className="bg-white px-4 py-3 shadow-sm flex justify-between items-center">
        <div className="text-gray-600 flex items-center">
          <FileText className="w-4 h-4 mr-1" />
          题目 {currentIndex + 1}/{questions.length}
          {!isRandomMode && (
            <button 
              onClick={toggleQuestionSelector}
              className="flex items-center text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors ml-2"
            >
              <Grid className="w-3 h-3 mr-1" />
              <span>选题</span>
            </button>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex h-7 bg-gray-100 rounded-full overflow-hidden">
            <button 
              onClick={() => isRandomMode && togglePracticeMode()}
              className={`flex items-center px-2 text-xs ${isRandomMode ? 'text-gray-500 hover:bg-gray-200' : 'bg-primary text-white'}`}
            >
              <List className="w-3 h-3 mr-1" />
              顺序
            </button>
            <button 
              onClick={() => !isRandomMode && togglePracticeMode()}
              className={`flex items-center px-2 text-xs ${!isRandomMode ? 'text-gray-500 hover:bg-gray-200' : 'bg-primary text-white'}`}
            >
              <Shuffle className="w-3 h-3 mr-1" />
              随机
            </button>
          </div>
        </div>
      </div>
      
      <div className="flex-1 p-4 pb-20">
        {currentQuestion ? (
          <div className="bg-white">
            {/* 题目区域 */}
            <div className="mb-6">
              <div className="bg-gray-50 p-3 border-l-4 border-blue-500 mb-4">
                {currentQuestion?.typename && (
                  <span className={`inline-flex items-center align-middle px-2 py-0.5 text-xs mr-2 ${
                    currentQuestion.typename === '单选' || currentQuestion.typename === '1' ? 'bg-blue-100 text-blue-600' :
                    currentQuestion.typename === '判断' || currentQuestion.typename === '2' ? 'bg-green-100 text-green-600' :
                    currentQuestion.typename === '多选' || currentQuestion.typename === '3' ? 'bg-purple-100 text-purple-600' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {getTypeName(currentQuestion.typename || currentQuestion.typecode || '未知')}
                  </span>
                )}
                <span className="font-bold">{currentIndex + 1}.</span> {currentQuestion.title}
              </div>
              
              {currentQuestion.content && (
                <div className="text-gray-600 mb-4 pl-3">
                  {currentQuestion.content}
                </div>
              )}
            
              {/* 选项区域 */}
              <div className="mt-5">
                <div className="text-sm text-gray-500 mb-2 pl-2">选择答案：</div>
                <div className="space-y-2">
                  {options.map((option) => {
                    // 获取选项的状态样式
                    let optionStyle = 'border-gray-200 hover:bg-gray-50';
                    let iconStyle = 'bg-gray-100 text-gray-500';
                    
                    // 如果用户已经选择了这个题目的答案
                    if (userAnswers[currentQuestion._id]) {
                      // 如果是当前选项
                      if (userAnswers[currentQuestion._id] === option.code) {
                        // 根据是否正确设置样式
                        if (option.value === "1") {
                          // 正确答案
                          optionStyle = 'bg-green-50 border-green-500';
                          iconStyle = 'bg-green-500 text-white';
                        } else {
                          // 错误答案
                          optionStyle = 'bg-red-50 border-red-500';
                          iconStyle = 'bg-red-500 text-white';
                        }
                      } 
                      // 如果不是当前选项，但是正确答案，且用户已经答错了，显示正确答案
                      else if (option.value === "1" && answeredStatus[currentQuestion._id] === 'incorrect') {
                        optionStyle = 'bg-green-50 border-green-200';
                        iconStyle = 'bg-green-200 text-green-700';
                      }
                    }
                    
                    return (
                      <div 
                        key={option.code}
                        onClick={() => !userAnswers[currentQuestion._id] && handleAnswer(currentQuestion._id, option.code, option.value === "1")}
                        className={`p-3 border rounded-lg ${optionStyle} ${userAnswers[currentQuestion._id] ? '' : 'cursor-pointer'}`}
                      >
                        <div className="flex items-start">
                          <div className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center mr-3 ${iconStyle}`}>
                            {option.code}
                          </div>
                          <div className="flex-1 text-gray-700 pt-1">
                            {option.content}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            
            {/* 解析区域 - 用户回答后显示，无论对错 */}
            {shouldShowExplanation && (
              <div className="bg-yellow-50 p-4 border border-yellow-200 mb-4">
                <h4 className="font-medium text-yellow-800 mb-1">解析</h4>
                <p className="text-sm text-yellow-700">
                  {currentQuestion.comments || '暂无解析'}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 bg-white">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">暂无题目</h3>
            <p className="text-gray-500 mb-6">该学科下暂时没有题目</p>
            <button
              onClick={() => navigate('/question-bank')}
              className="px-6 py-2 bg-primary text-white font-medium shadow-sm hover:bg-primary-dark transition-colors"
            >
              返回题库
            </button>
          </div>
        )}
      </div>

      {/* 题目选择器弹窗 */}
      {showQuestionSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b flex justify-between items-center">
              <h3 className="font-medium">选择题目</h3>
              <button onClick={toggleQuestionSelector} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1">
              {/* 按题型分组显示题目 */}
              {typeOrder.map(type => {
                // 检查当前题型是否有题目
                const typeQuestions = questionsByType[type];
                if (!typeQuestions || typeQuestions.length === 0) return null;
                
                // 获取题型显示样式
                const typeStyle = type === '单选' || type === '1' 
                  ? 'bg-blue-100 text-blue-600' 
                  : type === '判断' || type === '2'
                    ? 'bg-green-100 text-green-600'
                    : type === '多选' || type === '3'
                      ? 'bg-purple-100 text-purple-600'
                      : 'bg-gray-100 text-gray-600';
                
                return (
                  <div key={type} className="mb-4">
                    <div className={`px-3 py-1 mb-2 inline-block rounded-full text-sm ${typeStyle}`}>
                      {getTypeName(type)}
                    </div>
                    
                    <div className="grid grid-cols-5 sm:grid-cols-8 gap-2">
                      {typeQuestions.map(question => {
                        const isAnswered = userAnswers[question._id] && userAnswers[question._id] !== '';
                        const isCurrent = currentIndex === question.index;
                        const isCorrect = answeredStatus[question._id] === 'correct';
                        
                        let buttonStyle = 'border-gray-200 text-gray-600 hover:bg-gray-50';
                        
                        if (isCurrent) {
                          buttonStyle = 'border-blue-500 bg-blue-50 text-blue-600 font-bold';
                        } else if (isAnswered) {
                          buttonStyle = isCorrect 
                            ? 'border-green-300 bg-green-50 text-green-600' 
                            : 'border-red-300 bg-red-50 text-red-600';
                        }
                        
                        return (
                          <button
                            key={question._id}
                            onClick={() => jumpToQuestion(question.index)}
                            className={`h-10 flex items-center justify-center border ${buttonStyle}`}
                          >
                            {question.index + 1}
                            {isAnswered && (
                              <div className={`w-1.5 h-1.5 rounded-full absolute top-1 right-1 ${
                                isCorrect ? 'bg-green-500' : 'bg-red-500'
                              }`}></div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              
              {/* 处理其他类型的题目 */}
              {Object.keys(questionsByType)
                .filter(type => !typeOrder.includes(type))
                .map(type => {
                  const typeQuestions = questionsByType[type];
                  
                  return (
                    <div key={type} className="mb-4">
                      <div className="px-3 py-1 mb-2 inline-block rounded-full text-sm bg-gray-100 text-gray-600">
                        {getTypeName(type)}
                      </div>
                      
                      <div className="grid grid-cols-5 sm:grid-cols-8 gap-2">
                        {typeQuestions.map(question => {
                          const isAnswered = userAnswers[question._id] && userAnswers[question._id] !== '';
                          const isCurrent = currentIndex === question.index;
                          const isCorrect = answeredStatus[question._id] === 'correct';
                          
                          let buttonStyle = 'border-gray-200 text-gray-600 hover:bg-gray-50';
                          
                          if (isCurrent) {
                            buttonStyle = 'border-blue-500 bg-blue-50 text-blue-600 font-bold';
                          } else if (isAnswered) {
                            buttonStyle = isCorrect 
                              ? 'border-green-300 bg-green-50 text-green-600' 
                              : 'border-red-300 bg-red-50 text-red-600';
                          }
                          
                          return (
                            <button
                              key={question._id}
                              onClick={() => jumpToQuestion(question.index)}
                              className={`h-10 flex items-center justify-center border ${buttonStyle}`}
                            >
                              {question.index + 1}
                              {isAnswered && (
                                <div className={`w-1.5 h-1.5 rounded-full absolute top-1 right-1 ${
                                  isCorrect ? 'bg-green-500' : 'bg-red-500'
                                }`}></div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
            
            <div className="px-4 py-3 border-t">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center">
                  <span className="w-3 h-3 bg-green-50 border border-green-300 mr-1"></span>
                  <span>正确: {Object.values(answeredStatus).filter(v => v === 'correct').length}</span>
                </div>
                <div className="flex items-center">
                  <span className="w-3 h-3 bg-red-50 border border-red-300 mr-1"></span>
                  <span>错误: {Object.values(answeredStatus).filter(v => v === 'incorrect').length}</span>
                </div>
                <div className="flex items-center">
                  <span className="w-3 h-3 bg-white border border-gray-200 mr-1"></span>
                  <span>未答: {questions.length - Object.values(userAnswers).filter(v => v !== '').length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 底部固定导航栏 - 只保留上一题下一题按钮 */}
      {currentQuestion && (
        <div className="fixed bottom-0 bg-white border-t border-gray-200 flex justify-between items-center px-4 py-3 z-10 w-full max-w-[450px]">
          <button
            onClick={handlePrevQuestion}
            disabled={currentIndex === 0}
            className={`px-4 py-2 flex items-center ${
              currentIndex === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700'
            }`}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            上一题
          </button>
          
          <button
            onClick={handleNextQuestion}
            className="px-4 py-2 flex items-center text-gray-700"
          >
            {currentIndex < questions.length - 1 ? '下一题' : '完成'}
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      )}
    </div>
  );
}

export default QuestionPractice;